/**
 * 広告と購入の結果を、遊び方の記録（src/meta/telemetry.ts）に残すための薄い包み。
 *
 * 中身の Platform の動きは一切変えない。記録が失敗しても広告・購入には影響しない。
 */

import { track } from '../meta/telemetry';
import { onAdCovering } from './adLifecycle';
import type { Platform, PurchaseOutcome, RewardedPlacement } from './Platform';
import type { ProductId } from './products';

export function withTracking(inner: Platform): Platform {
  // Web版は広告も課金もないので包まない
  if (!inner.monetized) return inner;
  return {
    get kind() {
      return inner.kind;
    },
    get monetized() {
      return inner.monetized;
    },
    get canPurchase() {
      return inner.canPurchase;
    },
    init: () => inner.init(),
    async askTrackingPermission() {
      const result = await inner.askTrackingPermission();
      // ダイアログを出したとき（1台に1回）と、制限されていて出せなかったときだけ数える
      if (result && (result.prompted || result.status === 'restricted')) track('att', { detail: result.status });
      return result;
    },
    ownedProducts: () => inner.ownedProducts(),
    restorePurchases: () => inner.restorePurchases(),
    priceOf: (id: ProductId) => inner.priceOf(id),
    refreshPrices: () => inner.refreshPrices(),
    requestReview: () => inner.requestReview(),
    get canNotify() {
      return inner.canNotify;
    },
    requestNotificationPermission: () => inner.requestNotificationPermission(),
    scheduleNotices: (list) => inner.scheduleNotices(list),
    cancelNotices: () => inner.cancelNotices(),
    onNoticeOpened: (handler) => inner.onNoticeOpened(handler),
    adDiagnostics: () => inner.adDiagnostics?.() ?? null,
    async showRewarded(placement: RewardedPlacement): Promise<boolean> {
      const ok = await inner.showRewarded(placement);
      if (ok) track('ad_rewarded', { detail: placement });
      return ok;
    },
    async showInterstitial(): Promise<boolean | void> {
      // アプリでは「読み込めていなければ出さない」ので、実際に画面を覆ったときだけ数える。
      // モック（staging）は覆った合図を出さないので、呼んだら出たことにする。
      let shown = inner.kind === 'mock';
      const off = onAdCovering((covering) => {
        if (covering) shown = true;
      });
      let result: boolean | void;
      try {
        result = await inner.showInterstitial();
      } finally {
        off();
      }
      if (shown) track('ad_interstitial');
      return result;
    },
    async purchase(productId: ProductId): Promise<PurchaseOutcome> {
      const outcome = await inner.purchase(productId);
      if (outcome === 'purchased') track('purchase', { detail: productId });
      else if (outcome === 'cancelled') track('purchase_cancel', { detail: productId });
      else if (outcome === 'failed') track('purchase_fail', { detail: productId });
      return outcome;
    },
  };
}
